import { useEffect, useState, useContext } from "react";
import { Card, ListGroup, Badge, Alert } from 'react-bootstrap';
import Spinner from 'react-bootstrap/Spinner';
import $ from "jquery";
import { useNavigate } from "react-router-dom";
import UserContext from "../../contexts/UserContext";

const MenuList = () => {
    const navigate = useNavigate();
    const { user, setUser } = useContext(UserContext);
    const [menus, setMenus] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [noMenuMessage, setNoMenuMessage] = useState(false);

    const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    const getUserData = async (userId) => {
        try {
            const response = await $.ajax({
                type: "GET",
                url: `${process.env.REACT_APP_API_URL}/getUsers/${userId}`,
                xhrFields: { withCredentials: true },
                crossDomain: true
            });

            if (response && response.user) {
                setUser(response.user);
            } else {
                setUser(null);
                navigate("/login", { state: { message: response.message } });
            }
        } catch (error) {
            console.log("Error getting user:", error);
            navigate("/login");
        }
    };

    const getAvailableMenus = async () => {
        setIsLoading(true);
        try {
            const response = await $.ajax({
                type: "GET",
                url: `${process.env.REACT_APP_API_URL}/menus/available`,
                xhrFields: { withCredentials: true },
                crossDomain: true
            });

            if (response.menus && response.menus.length > 0) {
                setMenus(response.menus);
                setNoMenuMessage(false);
            } else {
                setMenus([]);
                setNoMenuMessage(true);
            }
        } catch (error) {
            console.log("Error fetching menus:", error);
            if (error.status === 401) {
                navigate("/login");
            } else {
                setNoMenuMessage(true);
            }
        }
        setIsLoading(false);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        if (date.toDateString() === today.toDateString()) {
            return "Today";
        } else if (date.toDateString() === tomorrow.toDateString()) {
            return "Tomorrow";
        } else {
            return `${weekDays[date.getDay()]}, ${date.toLocaleDateString()}`;
        }
    };

    const isDateInPast = (dateString) => {
        const date = new Date(dateString);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        date.setHours(0, 0, 0, 0);
        return date < today;
    };

    useEffect(() => {
        try {
            if (!user && localStorage.getItem('userSession')) {
                getUserData(JSON.parse(localStorage.getItem('userSession')));
            } else if (!user) {
                navigate("/login");
                return;
            }
        } catch (e) {
            if (!user) {
                navigate("/login");
                return;
            }
        }
        
        getAvailableMenus();
    }, [user]);

    if (isLoading) {
        return (
            <div className="mx-auto mt-5 text-center" style={{width: "100px"}}>
                <Spinner style={{color:'brown'}} animation="border" role="status" />
                <br />
                <span>Loading menus...</span>
            </div>
        );
    }

    return (
        <div className="container-fluid px-3">
            <h6 className="m-3 text-center" style={{color: '#884A39'}}>Available Menus</h6>
            
            {noMenuMessage && (
                <Alert variant='info' className="mx-3">
                    No menus available at the moment. Please check back later!
                </Alert>
            )}

            {menus.map((menu, index) => {
                const isPast = isDateInPast(menu.date);
                const hasLunch = menu.lunch?.isActive && menu.lunch.items?.length > 0;
                const hasDinner = menu.dinner?.isActive && menu.dinner.items?.length > 0;
                
                return (
                    <Card key={index} className={`mb-3 mx-2 ${isPast ? 'opacity-75' : ''}`}>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <span style={{color: '#fff', fontWeight: 'normal'}}>
                                {formatDate(menu.date)}
                            </span>
                            <div>
                                {hasLunch && <Badge bg="warning" className="me-1">Lunch</Badge>}
                                {hasDinner && <Badge bg="info">Dinner</Badge>}
                                {!hasLunch && !hasDinner && <Badge bg="secondary">No Thaali</Badge>}
                            </div>
                        </Card.Header>
                        
                        <ListGroup variant="flush">
                            {/* Lunch Section */}
                            {(hasLunch || (!menu.lunch?.isActive && menu.lunch?.comment)) && (
                                <ListGroup.Item>
                                    <div className="d-flex align-items-center mb-2">
                                        <strong style={{color: 'white'}}>Lunch:</strong>
                                    </div>
                                    {hasLunch ? (
                                        <div>
                                            {menu.lunch.items.map((item, idx) => (
                                                <div key={idx} className="ms-3" style={{color: '#fff3cd'}}>
                                                    • {item}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-muted ms-3">
                                            No Thaali {menu.lunch?.comment ? `- ${menu.lunch.comment}` : ''}
                                        </div>
                                    )}
                                </ListGroup.Item>
                            )}
                            
                            {/* Dinner Section */}
                            {(hasDinner || (!menu.dinner?.isActive && menu.dinner?.comment)) && (
                                <ListGroup.Item>
                                    <div className="d-flex align-items-center mb-2">
                                        <strong style={{color: 'white'}}>Dinner:</strong>
                                    </div>
                                    {hasDinner ? (
                                        <div>
                                            {menu.dinner.items.map((item, idx) => (
                                                <div key={idx} className="ms-3" style={{color: '#fff3cd'}}>
                                                    • {item}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-muted ms-3">
                                            No Thaali {menu.dinner?.comment ? `- ${menu.dinner.comment}` : ''}
                                        </div>
                                    )}
                                </ListGroup.Item>
                            )}
                        </ListGroup>
                        
                        {isPast && (
                            <Card.Footer>
                                <small className="text-muted">This menu has passed</small>
                            </Card.Footer>
                        )}
                    </Card>
                );
            })}
            
            {menus.length === 0 && !noMenuMessage && (
                <div className="text-center mt-4">
                    <p className="text-muted">No menus to display</p>
                </div>
            )}
        </div>
    );
};

export default MenuList;